<template>
  <div
    class="ct-form-rows teprosif-grid"
    :class="{ 'teprosif-grid-compact': step === 1 || corrected }"
  >
    <div
      v-for="item in items"
      :key="item.key"
      class="ct-item-row"
      :class="{
        'has-value': rowHasValue(item),
        'tp-result': corrected,
        'tp-step1': !corrected && step === 1,
      }"
    >
      <!-- Paso 0: num · palabra · emisión · omitida en una sola fila -->
      <template v-if="!corrected && step === 0">
        <div class="teprosif-card-main">
          <div class="ct-num">{{ item.id }}</div>
          <div class="ct-word" :title="item.label">{{ item.label }}</div>
          <input
            v-model="item.emission"
            class="ct-emission"
            :class="{ 'ct-guide-blink': isGuideActive(item.id) }"
            placeholder="Emisión"
            :disabled="item.omitted"
            @focus="onEmissionFocus"
            @mousedown="onEmissionInteract"
            @input="onEmissionInput(item)"
            @blur="onEmissionCommit(item)"
          />
          <button
            type="button"
            class="ct-omit"
            :class="{ active: item.omitted }"
            @click="toggleOmit(item)"
          >
            Omitida
          </button>
        </div>
      </template>

      <!-- Paso 1 y corregido: una fila · solo procesos detectados -->
      <template v-else>
        <div class="teprosif-card-main teprosif-compact-row">
          <div class="ct-num">{{ item.id }}</div>
          <div class="ct-word teprosif-word-inline" :title="wordTitle(item)">
            {{ item.label }}
            <small v-if="!item.omitted && step === 1 && item.emission.trim()">
              {{ item.emission.trim() }}
            </small>
          </div>
          <div class="ct-process-inline">
            <span v-if="item.omitted" class="ct-proc-tag omitted">Omitida</span>
            <template v-else-if="processCodes(item).length">
              <!-- El servidor repite un código cuando detecta el mismo proceso
                   dos veces en la palabra (EA=E5, EB=E5). La clave lleva el
                   índice porque el código solo no es único: con :key="code"
                   Vue descarta los repetidos y la fila muestra menos procesos
                   de los que cuenta el puntaje. -->
              <span
                v-for="(code, index) in processCodes(item)"
                :key="code + '-' + index"
                class="ct-proc-tag"
                :class="'ct-proc-' + codeType(code)"
                :title="processLabel(code)"
              >{{ code }}</span>
            </template>
            <span v-else class="ct-proc-empty">—</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { TEPROSIF_ITEMS } from "../data/teprosifItems";
import { PROC_GROUPS } from "../data/teprosifProcessOptions";
import {
  submitTeprosifStep0,
  submitTeprosifStep1,
} from "@/services/testCorrectionAdapter";
import { extractTestRecordId } from "../utils/normalizeTestIds";

const SUFFIX_MAP = [
  { group: "est", suffixes: ["EA", "EB", "EC"] },
  { group: "asim", suffixes: ["AA", "AB", "AC"] },
  { group: "sust", suffixes: ["SA", "SB", "SC"] },
];

function processLabel(code) {
  for (let i = 0; i < PROC_GROUPS.length; i++) {
    const group = PROC_GROUPS[i];
    for (let j = 0; j < group.opts.length; j++) {
      if (group.opts[j].v === code) return group.opts[j].l;
    }
  }
  return code;
}

function createItems() {
  return TEPROSIF_ITEMS.map((item) => ({
    ...item,
    emission: "",
    omitted: false,
    slots: { est: ["", "", ""], asim: ["", "", ""], sust: ["", "", ""] },
  }));
}

export default {
  name: "TeprosifForm",
  inject: {
    corregirTestWorkspace: { default: null },
  },
  props: {
    testValue: { type: Number, required: true },
    age: { type: String, default: "" },
    initialAnswers: { type: Object, default: null },
  },
  data() {
    return {
      items: createItems(),
      step: 0,
      redpsi_id: null,
      testRecordId: null,
      processAnswers: {},
      corrected: false,
      loading: false,
    };
  },
  created() {
    this.hydrate(this.initialAnswers);
  },
  computed: {
    toolbarLabel() {
      if (this.corrected) return "Corregir emisiones";
      if (this.step === 1) return "Finalizar y corregir test";
      return "Corregir emisiones";
    },
    toolbarDisabled() {
      if (this.loading) return true;
      if (this.corrected) return true;
      if (!this.age) return true;
      return false;
    },
  },
  watch: {
    toolbarLabel: { immediate: true, handler: "emitToolbarMeta" },
    toolbarDisabled: { immediate: true, handler: "emitToolbarMeta" },
    loading: { handler: "emitToolbarMeta" },
  },
  methods: {
    emitToolbarMeta() {
      this.$emit("toolbar-meta", {
        label: this.toolbarLabel,
        disabled: this.toolbarDisabled,
        loading: this.loading,
      });
    },
    rowHasValue(item) {
      if (this.step === 0 && !this.corrected) {
        return Boolean(item.emission.trim()) || item.omitted;
      }
      if (item.omitted) return true;
      return this.processCodes(item).length > 0;
    },
    wordTitle(item) {
      if (item.omitted) return item.label + " · Omitida";
      const codes = this.processCodes(item);
      if (!codes.length) return item.label;
      return item.label + " · " + codes.map(processLabel).join(", ");
    },
    processLabel(code) {
      return processLabel(code);
    },
    codeType(code) {
      if (!code) return "";
      if (code.charAt(0) === "E") return "est";
      if (code.charAt(0) === "A") return "asim";
      return "sust";
    },
    onEmissionInput(item) {
      if (item.emission.trim()) item.omitted = false;
      this.corrected = false;
    },
    onEmissionFocus() {
      this.onEmissionInteract();
    },
    onEmissionInteract() {
      this.tryGuideAdvance();
    },
    onEmissionCommit(item) {
      if (item.emission.trim() || item.omitted) {
        this.tryGuideAdvance();
      }
    },
    isGuideActive(id) {
      const ws = this.corregirTestWorkspace;
      return ws && ws.isFieldGuideActive(id);
    },
    tryGuideAdvance() {
      const ws = this.corregirTestWorkspace;
      if (ws && ws.dismissFieldGuide) ws.dismissFieldGuide();
    },
    toggleOmit(item) {
      item.omitted = !item.omitted;
      if (item.omitted) item.emission = "";
      this.corrected = false;
      this.tryGuideAdvance();
    },
    processCodes(item) {
      return PROC_GROUPS.flatMap((group) =>
        item.slots[group.key].filter(Boolean)
      );
    },
    buildEmissionAnswers() {
      const answers = { edad: this.age, dislalias: [] };
      this.items.forEach((item) => {
        if (item.omitted) {
          answers[item.key] = "om";
        } else if (item.emission.trim()) {
          answers[item.key] = item.emission.trim();
        } else {
          answers[item.key] = item.key;
        }
      });
      return answers;
    },
    // Inversa de buildEmissionAnswers. Solo se restaura el paso 0 (emisiones y
    // omitidas): los procesos del paso 1 se arman con el `selec` que RedPsi
    // devuelve al corregir las emisiones, y ese `selec` no queda guardado. Por
    // eso editar TEPROSIF vuelve a pasar por el paso 0 — que es justo lo que
    // hace submit() de todas formas, encadenando paso 0 y paso 1.
    hydrate(stored) {
      if (!stored) return;
      const step0 = stored["step-0"];
      if (!step0) return;
      this.items.forEach((item) => {
        const raw = step0[item.key];
        if (raw == null) return;
        if (raw === "om") {
          item.omitted = true;
          item.emission = "";
          return;
        }
        // buildEmissionAnswers manda la propia clave del ítem cuando no se
        // escribió emisión (= lo dijo bien), así que eso vuelve a ser vacío.
        item.emission = String(raw) === item.key ? "" : String(raw);
      });
    },
    applyServerSelec(selec) {
      this.processAnswers = { ...selec };
      this.items.forEach((item) => {
        SUFFIX_MAP.forEach(({ group, suffixes }) => {
          suffixes.forEach((suffix, idx) => {
            const key = item.key + suffix;
            const val = selec[key];
            if (val && val !== "-") {
              this.$set(item.slots[group], idx, val);
            }
          });
        });
      });
    },
    buildFullAnswers() {
      const answers = this.buildEmissionAnswers();
      this.items.forEach((item) => {
        if (item.omitted) {
          SUFFIX_MAP.forEach(({ suffixes }) => {
            suffixes.forEach((suffix) => {
              answers[item.key + suffix] = "-";
            });
          });
          return;
        }
        SUFFIX_MAP.forEach(({ group, suffixes }) => {
          suffixes.forEach((suffix, idx) => {
            const key = item.key + suffix;
            const val = item.slots[group][idx];
            answers[key] = val || this.processAnswers[key] || "-";
          });
        });
      });
      return answers;
    },
    async runStep0() {
      const data = await submitTeprosifStep0({
        testValue: this.testValue,
        answers: this.buildEmissionAnswers(),
      });
      this.redpsi_id = data.redpsi_id;
      this.testRecordId = extractTestRecordId(data) || this.testRecordId;
      if (!this.redpsi_id) {
        throw new Error(
          "El servidor no devolvió redpsi_id. No se puede finalizar TEPROSIF."
        );
      }
      if (data.selec) this.applyServerSelec(data.selec);
      this.step = 1;
      return data;
    },
    async runStep1() {
      if (!this.redpsi_id) {
        throw new Error(
          "Falta redpsi_id para finalizar TEPROSIF. Corrige de nuevo las emisiones."
        );
      }
      const data = await submitTeprosifStep1({
        testValue: this.testValue,
        answers: this.buildFullAnswers(),
        redpsi_id: this.redpsi_id,
      });
      const finalId = extractTestRecordId(data) || this.testRecordId;
      if (!finalId) {
        throw new Error(
          "No se recibió el ID del test corregido. No se puede descargar el informe."
        );
      }
      this.corrected = true;
      this.$emit("corrected", Object.assign({}, data, { id: finalId }));
      return data;
    },
    async submit() {
      if (this.loading || this.corrected) return;
      if (!this.age) {
        const ws = this.corregirTestWorkspace;
        if (ws) ws.guidePhase = "age";
        if (this.$toasted) {
          this.$toasted.error("Selecciona la edad antes de corregir.");
        }
        return;
      }
      this.loading = true;
      try {
        if (this.step === 0) {
          await this.runStep0();
          await this.runStep1();
        } else {
          await this.runStep1();
        }
      } catch (err) {
        const msg =
          (err && err.message) ||
          "No se pudo corregir TEPROSIF. Revisa los datos.";
        if (this.$toasted) this.$toasted.error(msg);
      } finally {
        this.loading = false;
      }
    },
    reset() {
      this.items = createItems();
      this.step = 0;
      this.redpsi_id = null;
      this.testRecordId = null;
      this.processAnswers = {};
      this.corrected = false;
      this.loading = false;
    },
  },
};
</script>
